import {
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  makeStyles,
} from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import {
  ALL_DEPLOY_GROUPS,
  Category,
  GroupFilter,
  ViewPattern,
} from './filter';

const useStyles = makeStyles(theme => ({
  bar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
  },
  groupSelect: {
    minWidth: 120,
  },
  maintenanceLabel: {
    marginLeft: 0,
    '& .MuiFormControlLabel-label': {
      fontSize: '0.84rem',
      color: theme.palette.text.secondary,
    },
  },
}));

export const MergeOpsFilterBar = ({
  value,
  onChange,
  viewPattern,
  onViewPatternChange,
  deployGroups,
  deployGroup,
  onDeployGroupChange,
}: {
  value: GroupFilter;
  onChange: (next: GroupFilter) => void;
  viewPattern: ViewPattern;
  onViewPatternChange: (next: ViewPattern) => void;
  deployGroups: string[];
  deployGroup: string;
  onDeployGroupChange: (next: string) => void;
}) => {
  const classes = useStyles();
  return (
    <Box className={classes.bar}>
      {/* Only teams that actually declare deployment groups see the selector;
          with no annotated component the bar is unchanged. */}
      {deployGroups.length > 0 && (
        <Select
          className={classes.groupSelect}
          variant="outlined"
          margin="dense"
          value={deployGroup}
          onChange={e => onDeployGroupChange(e.target.value as string)}
          inputProps={{ 'aria-label': 'Deployment group' }}
        >
          {/* "all" ignores groups: the team's whole fleet, and the default.
              A real group named "all" would collide with the pseudo-group, so
              it is dropped here (selecting "all" shows it anyway). */}
          <MenuItem value={ALL_DEPLOY_GROUPS}>{ALL_DEPLOY_GROUPS}</MenuItem>
          {deployGroups
            .filter(name => name !== ALL_DEPLOY_GROUPS)
            .map(name => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
        </Select>
      )}
      {/* Exclusive category: Cloud Run, Terraform, or Both together — "Both"
          lets one deploy group ship Cloud Run and Terraform in a single run. */}
      <ToggleButtonGroup
        size="small"
        exclusive
        value={value.category}
        onChange={(_e, next: Category | null) =>
          next && onChange({ ...value, category: next })
        }
      >
        <ToggleButton value="cloudrun">Cloud Run</ToggleButton>
        <ToggleButton value="terraform">Terraform</ToggleButton>
        <ToggleButton value="both">Both</ToggleButton>
      </ToggleButtonGroup>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={viewPattern}
        onChange={(_e, next: ViewPattern | null) =>
          next && onViewPatternChange(next)
        }
      >
        <ToggleButton value="split">Promote</ToggleButton>
        <ToggleButton value="combined">Shift all</ToggleButton>
      </ToggleButtonGroup>
      {/* Maintenance-mode components are shown by default; unticking this
          narrows the view to actively developed components. */}
      <FormControlLabel
        className={classes.maintenanceLabel}
        control={
          <Checkbox
            size="small"
            checked={value.includeMaintenance !== false}
            onChange={(_e, checked) =>
              onChange({ ...value, includeMaintenance: checked })
            }
          />
        }
        label="Include maintenance"
      />
    </Box>
  );
};
